import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import Swal from "sweetalert2";
import { apiRequest, formatDate } from "../../core/common/api";
import MetricCard from "../../shared/MetricCard";
import PaginationBar from "../../shared/PaginationBar";
import ResetButton from "../../shared/ResetButton";
import useTableSearch from "../../shared/useTableSearch";

type FeedbackFilters = {
  search: string;
  rating: string;
};

const EMPTY_SUMMARY = { total: 0, averageRating: 0, positive: 0, negative: 0 };

const ratingTone = (rating: number) => (rating >= 4 ? "success" : rating <= 2 ? "danger" : "warning");

const OrderFeedback = () => {
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState<FeedbackFilters>({ search: "", rating: "all" });
  const searchTerm = useTableSearch(filters.search);

  const loadFeedbacks = useCallback(async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (searchTerm) params.set("search", searchTerm);
      if (filters.rating !== "all") params.set("rating", filters.rating);

      const result = await apiRequest(`/order-feedbacks?${params}`, "GET");
      setFeedbacks(result.feedbacks || []);
      setSummary(result.summary || EMPTY_SUMMARY);
      setTotal(result.pagination?.total || 0);
      setTotalPages(result.pagination?.totalPages || 1);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load order feedback", "error");
    } finally {
      setLoading(false);
    }
  }, [filters.rating, searchTerm, page, limit]);

  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const resetFilters = () => {
    setFilters({ search: "", rating: "all" });
  };

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">CUSTOMER FEEDBACK</div>
          <h1 className="page-title">
            Order <em>Feedback</em>
          </h1>
          <div className="page-sub">Feedback shared by customers after their orders were delivered.</div>
        </div>
      </div>

      <section className="metrics-grid">
        <MetricCard metric={{ label: "Total Feedback", value: summary.total, icon: "💬", tone: "blue" }} />
        <MetricCard metric={{ label: "Average Rating", value: summary.averageRating, icon: "⭐", tone: "orange" }} />
        <MetricCard metric={{ label: "Positive (4-5)", value: summary.positive, icon: "😊", tone: "purple" }} />
        <MetricCard metric={{ label: "Negative (1-2)", value: summary.negative, icon: "⚠️", tone: "blue" }} />
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <Search size={18} aria-hidden="true" />
          <input
            value={filters.search}
            onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            placeholder="Search order code, customer or comment..."
          />
        </div>

        <select
          className="filter-select"
          value={filters.rating}
          onChange={(event) => setFilters({ ...filters, rating: event.target.value })}
        >
          <option value="all">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </select>

        <ResetButton onClick={resetFilters} />
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Order Feedback ({total})</h2>
          <p>One feedback per delivered order.</p>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Order</th>
                <th>Rating</th>
                <th>Comment</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="empty-state">Loading feedback...</td>
                </tr>
              ) : feedbacks.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">No feedback matches these filters.</td>
                </tr>
              ) : (
                feedbacks.map((feedback) => (
                  <tr key={feedback._id}>
                    <td>
                      <strong>{feedback.user?.name || "Deleted user"}</strong>
                      <small className="table-muted">{feedback.user?.email || feedback.user?.phone || "-"}</small>
                    </td>
                    <td>
                      {feedback.order?._id ? (
                        <Link to={`/orders/${feedback.order._id}`} className="order-code">
                          {feedback.order.orderCode || feedback.orderCode}
                        </Link>
                      ) : (
                        feedback.orderCode || "-"
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${ratingTone(Number(feedback.rating))}`}>
                        <span aria-hidden="true">⭐</span> {feedback.rating}/5
                      </span>
                    </td>
                    <td>{feedback.comment || "-"}</td>
                    <td>{formatDate(feedback.createdAt)}</td>
                    <td>
                      {feedback.order?._id ? (
                        <Link to={`/orders/${feedback.order._id}`} className="btn-sm btn-outline">
                          View
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={page}
          limit={limit}
          total={total}
          totalPages={totalPages}
          itemLabel="feedback"
          pageSizeOptions={[10, 25, 50, 100]}
          onPageChange={setPage}
          onLimitChange={(nextLimit) => {
            setPage(1);
            setLimit(nextLimit);
          }}
        />
      </section>
    </>
  );
};

export default OrderFeedback;
